import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { bannerApi } from '../../api/banner';

// 分页查询轮播图列表
export const fetchBanners = createAsyncThunk(
  'banner/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await bannerApi.pageBanners({
        page: params.page || 1,
        pageSize: params.pageSize || 10,
        title: params.title,
        linkType: params.linkType,
        status: params.status,
        ...params
      });
      
      if (response && response.code === 200) {
        const data = response.data || response;
        return {
          records: data.records || data.list || [],
          total: data.total || 0,
          current: data.current || params.page || 1,
          size: data.size || params.pageSize || 10
        };
      }
      return rejectWithValue(response?.message || '获取轮播图列表失败');
    } catch (error) {
      message.error('获取轮播图列表失败');
      return rejectWithValue(error.message || '获取轮播图列表失败');
    }
  }
);

// 获取轮播图详情
export const fetchBannerDetail = createAsyncThunk(
  'banner/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await bannerApi.getBannerById(id);
      if (response && response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response?.message || '获取轮播图详情失败');
    } catch (error) {
      message.error('获取轮播图详情失败');
      return rejectWithValue(error.message || '获取轮播图详情失败');
    }
  }
);

// 创建轮播图
export const createBanner = createAsyncThunk(
  'banner/create',
  async (data, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await bannerApi.createBanner(data);
      if (response && response.code === 200) {
        message.success('创建轮播图成功');
        // 重新获取列表
        const { banner } = getState();
        const { bannerList } = banner;
        dispatch(fetchBanners({
          page: bannerList.current,
          pageSize: bannerList.size
        }));
        return response.data;
      }
      message.error(response?.message || '创建轮播图失败');
      return rejectWithValue(response?.message || '创建轮播图失败');
    } catch (error) {
      message.error('创建轮播图失败');
      return rejectWithValue(error.message || '创建轮播图失败');
    }
  }
);

// 更新轮播图
export const updateBanner = createAsyncThunk(
  'banner/update',
  async ({ id, data }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await bannerApi.updateBanner(id, data);
      if (response && response.code === 200) {
        message.success('更新轮播图成功');
        // 重新获取列表
        const { banner } = getState();
        const { bannerList } = banner;
        dispatch(fetchBanners({
          page: bannerList.current,
          pageSize: bannerList.size
        }));
        return response.data;
      }
      message.error(response?.message || '更新轮播图失败');
      return rejectWithValue(response?.message || '更新轮播图失败');
    } catch (error) {
      message.error('更新轮播图失败');
      return rejectWithValue(error.message || '更新轮播图失败');
    }
  }
);

// 删除轮播图
export const deleteBanner = createAsyncThunk(
  'banner/delete',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await bannerApi.deleteBanner(id);
      if (response && response.code === 200) {
        message.success('删除轮播图成功');
        // 重新获取列表
        const { banner } = getState();
        const { bannerList } = banner;
        dispatch(fetchBanners({
          page: bannerList.current,
          pageSize: bannerList.size
        }));
        return id;
      }
      message.error(response?.message || '删除轮播图失败');
      return rejectWithValue(response?.message || '删除轮播图失败');
    } catch (error) {
      message.error('删除轮播图失败');
      return rejectWithValue(error.message || '删除轮播图失败');
    }
  }
);

// 获取启用的轮播图列表
export const fetchEnabledBanners = createAsyncThunk(
  'banner/fetchEnabled',
  async (_, { rejectWithValue }) => {
    try {
      const response = await bannerApi.getEnabledBanners();
      if (response && response.code === 200) {
        return response.data || [];
      }
      return rejectWithValue(response?.message || '获取启用的轮播图列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取启用的轮播图列表失败');
    }
  }
);

// 初始状态
const initialState = {
  bannerList: {
    records: [],
    total: 0,
    current: 1,
    size: 10,
    loading: false,
    error: null
  },
  bannerDetail: {
    data: null,
    loading: false,
    error: null
  },
  enabledBanners: {
    data: [],
    loading: false,
    error: null
  },
  operationStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建slice
const bannerSlice = createSlice({
  name: 'banner',
  initialState,
  reducers: {
    clearBannerDetail: (state) => {
      state.bannerDetail = {
        data: null,
        loading: false,
        error: null
      };
    },
    clearOperationStatus: (state) => {
      state.operationStatus = {
        loading: false,
        success: false,
        error: null
      };
    },
    setPagination: (state, action) => {
      if (action.payload.current !== undefined) {
        state.bannerList.current = action.payload.current;
      }
      if (action.payload.size !== undefined) {
        state.bannerList.size = action.payload.size;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // 分页查询列表
      .addCase(fetchBanners.pending, (state) => {
        state.bannerList.loading = true;
        state.bannerList.error = null;
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.bannerList.loading = false;
        state.bannerList.records = action.payload.records;
        state.bannerList.total = action.payload.total;
        state.bannerList.current = action.payload.current;
        state.bannerList.size = action.payload.size;
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.bannerList.loading = false;
        state.bannerList.error = action.payload;
      })
      // 获取详情
      .addCase(fetchBannerDetail.pending, (state) => {
        state.bannerDetail.loading = true;
        state.bannerDetail.error = null;
      })
      .addCase(fetchBannerDetail.fulfilled, (state, action) => {
        state.bannerDetail.loading = false;
        state.bannerDetail.data = action.payload;
      })
      .addCase(fetchBannerDetail.rejected, (state, action) => {
        state.bannerDetail.loading = false;
        state.bannerDetail.error = action.payload;
      })
      // 创建轮播图
      .addCase(createBanner.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(createBanner.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(createBanner.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 更新轮播图
      .addCase(updateBanner.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(updateBanner.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 删除轮播图
      .addCase(deleteBanner.pending, (state) => {
        state.operationStatus.loading = true;
        state.operationStatus.success = false;
        state.operationStatus.error = null;
      })
      .addCase(deleteBanner.fulfilled, (state) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = true;
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.operationStatus.loading = false;
        state.operationStatus.success = false;
        state.operationStatus.error = action.payload;
      })
      // 获取启用的轮播图列表
      .addCase(fetchEnabledBanners.pending, (state) => {
        state.enabledBanners.loading = true;
        state.enabledBanners.error = null;
      })
      .addCase(fetchEnabledBanners.fulfilled, (state, action) => {
        state.enabledBanners.loading = false;
        state.enabledBanners.data = action.payload;
      })
      .addCase(fetchEnabledBanners.rejected, (state, action) => {
        state.enabledBanners.loading = false;
        state.enabledBanners.error = action.payload;
      });
  }
});

export const {
  clearBannerDetail,
  clearOperationStatus,
  setPagination
} = bannerSlice.actions;

export default bannerSlice.reducer;

